// #3392 : UPS 레이블 출력에 실제 포장된 홀박스 목록과 (선택 시) 홀박스 레이블을 붙이는 흐름.
// 출력 단계(tier): full = UPS + 목록 + 레이블, list = UPS + 목록, labels = UPS + 레이블(목록 없음), base = 오늘과 같은 UPS 출력, none = 기본 템플릿 실패(오늘과 같음).
// 목록·레이블은 각각 체크(want_list·want_labels)했을 때만 넣는다.
// 부가 출력은 검증된 결과만 채택하고, 실패하면 이미 검증된 단계로 내려간다. 커밋·이력·toast 는 실행마다 각각 최대 1회다.
// 의존성은 deps 로만 받는다(@/api·router·store 를 import 하지 않는다).
// 3차: 렌더·검증 순서는 그대로 두고, 커밋 직전에 렌더된 쪽만 재배치한다(목록 첫 쪽, 홀박스 레이블은 같은 박스 UPS 레이블 뒤).

import { WHOLE_BOX_TICKET_REQUIRED_NAMES, norm } from '@/utils/renderCompleteness';

export { norm };

export const WHOLE_BOX_LIST_PAGE_NAME = 'PageWholeBoxList';
export const LABEL_DROPPED_MESSAGE = 'Whole Box Label could not be added. UPS labels and Whole Box List were printed.';
export const LIST_DROPPED_MESSAGE = 'Whole Box List could not be added. Only UPS labels were printed.';
export const LABEL_ONLY_DROPPED_MESSAGE = 'Whole Box Label could not be added. Only UPS labels were printed.';
export const EXTRAS_DROPPED_MESSAGE = 'Whole Box List and Whole Box Label could not be added. Only UPS labels were printed.';
export const PRINT_FAILED_MESSAGE = 'UPS label could not be printed.';
// #3392 : 초기값. 부가 요청은 axios timeout, 렌더는 렌더별 감시 시간으로 따로 둔다(전체 기한 타이머는 없다).
export const UPS_WHOLE_BOX_REQUEST_TIMEOUT_MS = 15000;
export const UPS_WHOLE_BOX_RENDER_WATCHDOG_MS = 30000;

function compareText(a, b) {
	return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * #3392 : API 응답에서 요청한 인보이스만 요청 순서로 남기고, 실제 홀박스가 없는 인보이스는 뺀다.
 * result_code 가 success 가 아니거나 형식이 맞지 않으면 예외다(부가 출력 실패로 처리된다).
 */
export function normalizeWholeBoxInvoices(response_data, invoice_nos) {
	if (!response_data || response_data.result_code !== 'success' || !Array.isArray(response_data.data)) throw new Error('Whole box data request failed');
	const by_no = new Map();
	response_data.data.forEach(invoice => {
		if (invoice && invoice.invoice_no != null && !by_no.has(String(invoice.invoice_no))) by_no.set(String(invoice.invoice_no), invoice);
	});
	const seen = new Set();
	const invoices = [];
	(invoice_nos || []).forEach(invoice_no => {
		const key = String(invoice_no);
		if (seen.has(key)) return;
		seen.add(key);
		const invoice = by_no.get(key);
		if (!invoice || !Array.isArray(invoice.wholeBoxPrintList) || invoice.wholeBoxPrintList.length === 0) return;
		if (invoice.wholeBoxPrintList.some(row => !row || typeof row !== 'object')) throw new Error('Invalid whole box row: ' + key);
		invoices.push(invoice);
	});
	return invoices;
}

// #3392 : API 계약상 box_no 는 JSON 정수다. 문자열·혼합·중복이면 대응 불가(null)로 본다.
function distinctIntegerList(values, length) {
	if (!Array.isArray(values) || values.length !== length) return null;
	if (!values.every(v => typeof v === 'number' && Number.isInteger(v))) return null;
	if (new Set(values).size !== values.length) return null;
	return values.slice();
}

/**
 * #3392 : UPS 레이블 k(= image[k], box_no k+1)의 Packing Box box_no 목록. 대응을 믿을 수 없으면 null(홀박스 레이블은 끝에 둔다).
 * 조건: packageBoxNos·packageTrackNos 가 같은 길이 = image 수, image[k].box_no == k+1, image[k].track_no === packageTrackNos[k].
 * 열어 둔 팝업의 레이블이 다른 곳에서 Void·재생성된 경우를 거른다.
 */
export function normalizePackageMapping(response_data, images) {
	if (!response_data || typeof response_data !== 'object' || !Array.isArray(images) || images.length === 0) return null;
	const track_nos = response_data.packageTrackNos;
	if (!Array.isArray(track_nos) || track_nos.length !== images.length) return null;
	const box_nos = distinctIntegerList(response_data.packageBoxNos, images.length);
	if (!box_nos) return null;
	for (let k = 0; k < images.length; k++) {
		const image = images[k];
		if (!image || Number(image.box_no) !== k + 1) return null;
		const track_no = track_nos[k];
		if (typeof track_no !== 'string' || track_no === '' || image.track_no == null || String(image.track_no) !== track_no) return null;
	}
	return box_nos;
}

/**
 * #3392 : 인보이스별 홀박스 box_no 목록(wholeBoxPrintList 와 같은 순서·길이). 반환 Map: String(invoice_no) → 숫자 배열 | null(그 인보이스는 대응 불가).
 */
export function normalizeWholeBoxNos(response_data, invoices) {
	const result = new Map();
	const by_invoice = response_data && typeof response_data === 'object' && response_data.wholeBoxNos && typeof response_data.wholeBoxNos === 'object' ? response_data.wholeBoxNos : null;
	(invoices || []).forEach(invoice => {
		const key = String(invoice.invoice_no);
		const values = by_invoice && Object.prototype.hasOwnProperty.call(by_invoice, key) ? by_invoice[key] : null;
		result.set(key, distinctIntegerList(values, Array.isArray(invoice.wholeBoxPrintList) ? invoice.wholeBoxPrintList.length : -1));
	});
	return result;
}

/**
 * #3392 : 렌더된 쪽의 새 순서(결합 인덱스 순열). 결합 인덱스는 UPS 보고서 쪽 0..U-1 뒤에 label_groups 순서대로 각 그룹 page_count 만큼 이어진다.
 * 순서: (1) 목록 쪽 (2) 첫 UPS 레이블 앞 안내 쪽 (3) UPS 레이블 k 마다 UPS k, 그 뒤 주 인보이스에서 box_no == package_box_nos[k] 인 홀박스 레이블 1장(있으면)
 * (4) 나머지 홀박스 레이블(그룹·쪽 순서)은 마지막 UPS 레이블 뒤. 주 인보이스는 main_invoice_no 로만 판별한다.
 * expect_list = false(레이블만 출력)면 목록 쪽이 없어야 하고 (1) 은 비어 있다.
 * 구성이 기대와 다르면 예외다(호출한 쪽이 base 로 내려간다).
 */
export function planUpsPageOrder({ page_names, list_page_name, ups_page_name = 'Page2', image_count, label_groups = [], main_invoice_no, package_box_nos, expect_list = true }) {
	if (!Array.isArray(page_names)) throw new Error('No page names');
	if (!Number.isInteger(image_count) || image_count < 1) throw new Error('Invalid UPS label count: ' + image_count);
	const page_count = page_names.length;
	const list_indexes = [];
	const ups_indexes = [];
	page_names.forEach((name, index) => {
		if (name === list_page_name) list_indexes.push(index);
		else if (name === ups_page_name) ups_indexes.push(index);
	});

	// #3392 : 목록을 출력하는 단계(list·full)는 목록 쪽이 보고서 끝에 연속, 레이블만 출력하는 단계(labels)는 목록 쪽이 없다.
	if (expect_list && list_indexes.length < 1) throw new Error('No list page');
	if (!expect_list && list_indexes.length > 0) throw new Error('Unexpected list page');
	const first_list = page_count - list_indexes.length;
	if (!list_indexes.every((index, k) => index === first_list + k)) throw new Error('List pages are not the last pages');
	if (ups_indexes.length !== image_count) throw new Error('UPS label pages ' + ups_indexes.length + ' != ' + image_count);
	const first_ups = ups_indexes[0];
	if (!ups_indexes.every((index, k) => index === first_ups + k)) throw new Error('UPS label pages are not contiguous');
	if (ups_indexes[image_count - 1] + 1 !== first_list) throw new Error('Other pages between UPS labels and list');
	if (package_box_nos != null && (!Array.isArray(package_box_nos) || package_box_nos.length !== image_count)) throw new Error('Package box count mismatch');

	// 레이블 쪽의 결합 인덱스
	const labels = [];
	let offset = page_count;
	(label_groups || []).forEach((group, group_index) => {
		if (!group || !Number.isInteger(group.page_count) || group.page_count < 0) throw new Error('Invalid label group ' + group_index);
		if (group.box_nos != null && (!Array.isArray(group.box_nos) || group.box_nos.length !== group.page_count)) throw new Error('Label box count mismatch in group ' + group_index);
		const is_main = main_invoice_no != null && String(group.invoice_no) === String(main_invoice_no);
		for (let i = 0; i < group.page_count; i++) {
			labels.push({ index: offset + i, box_no: is_main && group.box_nos ? group.box_nos[i] : null, used: false });
		}
		offset += group.page_count;
	});
	const total = offset;

	const order = [];
	list_indexes.forEach(index => order.push(index));
	for (let index = 0; index < first_ups; index++) order.push(index);
	ups_indexes.forEach((ups_index, k) => {
		order.push(ups_index);
		if (!package_box_nos) return;
		const label = labels.find(l => !l.used && l.box_no !== null && l.box_no === package_box_nos[k]);
		if (label) {
			label.used = true;
			order.push(label.index);
		}
	});
	labels.forEach(label => {
		if (!label.used) order.push(label.index);
	});

	// 불변식: 모든 쪽이 정확히 한 번, 마지막 UPS 레이블 앞에서는 UPS 레이블 하나 뒤에 홀박스 레이블 최대 1장
	if (order.length !== total || new Set(order).size !== total || !order.every(index => Number.isInteger(index) && index >= 0 && index < total)) throw new Error('Page order is not a permutation');
	const last_ups_position = order.indexOf(ups_indexes[image_count - 1]);
	let label_run = 0;
	for (let position = 0; position < last_ups_position; position++) {
		if (order[position] >= page_count) {
			if (++label_run > 1) throw new Error('More than one whole box label after a UPS label');
		} else {
			label_run = 0;
		}
	}
	return order;
}

/** #3392 : 홀박스 1개 = 1행인 wholeBoxPrintList 를 품목 클래스·이름·색상별 박스 수로 센다. 정렬 class_code, color_code(같으면 class_name). */
export function buildWholeBoxList(invoices) {
	const groups = new Map();
	invoices.forEach(invoice => {
		invoice.wholeBoxPrintList.forEach(row => {
			const class_code = norm(row.class_code);
			const class_name = norm(row.class_name);
			const color_code = norm(row.color_code);
			const key = JSON.stringify([class_code, class_name, color_code]);
			const group = groups.get(key);
			if (group) group.box_qty += 1;
			else groups.set(key, { class_code, class_name, color_code, box_qty: 1 });
		});
	});
	return Array.from(groups.values()).sort((a, b) => compareText(a.class_code, b.class_code) || compareText(a.color_code, b.color_code) || compareText(a.class_name, b.class_name));
}

function noop() {}

/**
 * #3392 : 실행 1회. 반환 { promise, abandonExtras }.
 * promise 는 reject 하지 않고 { tier, dropped } 로 끝난다. abandonExtras() 는 그 시점까지 검증된 최선 단계로 즉시 settle 한다.
 * opts = { print_data, invoice_nos, main_invoice_no, want_list, want_labels, request_timeout_ms, render_watchdog_ms }
 * want_list·want_labels 는 호출자가 체크 값으로 명시한다. 주지 않으면 넣지 않는다(목록은 체크했을 때만 출력).
 */
export function startUpsWholeBoxPrint(deps, opts) {
	const log = (...args) => {
		try {
			if (deps.log) deps.log(...args);
		} catch (e) {
			// 기록 실패는 무시한다.
		}
	};
	const notify = (level, message) => {
		try {
			if (deps.notify) deps.notify(level, message);
		} catch (e) {
			log('[#3392] notify failed', e);
		}
	};
	const print_data = opts.print_data;
	const invoice_nos = opts.invoice_nos || [];
	const main_invoice_no = opts.main_invoice_no;
	const want_list = !!opts.want_list;
	const want_labels = !!opts.want_labels;
	// #3392 : base 로 빠질 때 dropped 는 원한 부가 출력 중 바깥쪽(목록, 목록이 없으면 레이블)이다.
	const base_dropped = want_list ? 'list' : 'label';
	const request_timeout_ms = opts.request_timeout_ms || UPS_WHOLE_BOX_REQUEST_TIMEOUT_MS;
	const render_watchdog_ms = opts.render_watchdog_ms || UPS_WHOLE_BOX_RENDER_WATCHDOG_MS;

	let settled = false;
	let active_restore = null;
	let list_known = false; // 실제 홀박스가 있음을 확인했다(예기치 않은 예외로 빠질 때 경고 여부)
	let verified = null; // { report, data, page_names, has_list } : 미리 렌더해 검증한 UPS(+ 목록) 보고서(full·labels 면 레이블 쪽이 붙은 상태), page_names 는 검증한 쪽 이름
	let package_box_nos = null; // UPS 레이블 k 의 Packing Box box_no(대응 불가면 null)
	let label_groups = []; // 검증된 레이블 쪽 묶음 [{ invoice_no, page_count, box_nos }], 붙인 순서
	let resolve_run;
	const promise = new Promise(resolve => (resolve_run = resolve));

	// a. 기본 UPS 템플릿 파일은 오늘처럼 바로 받는다.
	let base_promise;
	try {
		base_promise = Promise.resolve(deps.loadUpsFile());
	} catch (e) {
		base_promise = Promise.reject(e);
	}
	base_promise.catch(noop);

	function releaseActiveRender() {
		const restore = active_restore;
		active_restore = null;
		if (restore) {
			try {
				restore();
			} catch (e) {
				log('[#3392] restore failed', e);
			}
		}
	}

	// 렌더는 한 번에 하나다. settle 뒤에는 새 렌더를 시작하지 않는다.
	async function renderTracked(report, data) {
		if (settled) throw new Error('Already settled');
		const handle = deps.render(report, data, render_watchdog_ms);
		const render_promise = handle && typeof handle.then === 'function' ? handle : handle && handle.promise;
		const restore = handle && typeof handle.restore === 'function' ? handle.restore : noop;
		active_restore = restore;
		try {
			await render_promise;
		} finally {
			if (active_restore === restore) active_restore = null;
			restore();
		}
	}

	// #3392 : 계획한 순서로 쪽 객체만 옮기고, 각 위치가 계획한 원래 객체인지(===)와 4x6 을 확인한다. 쪽 이름만으로 보지 않는다(UPS 는 모두 Page2, 레이블은 모두 Page3).
	function applyPageOrder(report, order) {
		const pages = deps.pageList(report);
		if (!Array.isArray(pages) || pages.length !== order.length) throw new Error('Rendered pages ' + (pages ? pages.length : 'none') + ' != ' + order.length);
		if (new Set(pages).size !== pages.length) throw new Error('Rendered pages are not distinct');
		const ordered = order.map(index => pages[index]);
		deps.reorderPages(report, ordered);
		const after = deps.pageList(report);
		if (!Array.isArray(after) || after.length !== ordered.length || !after.every((page, index) => page === ordered[index]) || new Set(after).size !== after.length) throw new Error('Page reorder check failed');
		const geometry = deps.verifyGeometry(deps.snapshot(report));
		if (!geometry || !geometry.ok) throw new Error('Page geometry check failed: ' + (geometry ? geometry.reason : 'no result'));
	}

	function commitAttempt(tier, base_file) {
		if (tier === 'base') {
			const template = deps.createUpsTemplate(base_file);
			deps.commitReport(deps.makeReport(template, print_data));
		} else {
			// #3392 : 커밋 직전에 재배치한다. 예외면 이 보고서를 버리고 commitTier 가 새 base 로 다시 커밋한다.
			const order = planUpsPageOrder({
				page_names: verified.page_names,
				list_page_name: WHOLE_BOX_LIST_PAGE_NAME,
				image_count: print_data.image.length,
				label_groups: tier === 'full' || tier === 'labels' ? label_groups : [],
				main_invoice_no,
				package_box_nos,
				expect_list: tier !== 'labels',
			});
			applyPageOrder(verified.report, order);
			deps.commitReport(deps.makeReport(verified.report, verified.data));
		}
	}

	async function commitTier(tier, dropped) {
		let base_file;
		try {
			base_file = await base_promise;
		} catch (e) {
			// 기본 템플릿 실패는 오늘과 같다(공용 인터셉터가 처리). 커밋·이력·경고 없음.
			log('[#3392] UPS label template failed', e);
			return { tier: 'none', dropped: null };
		}

		let final_tier = null;
		let final_dropped = dropped;
		const attempts = tier === 'base' ? ['base'] : [tier, 'base'];
		for (const attempt of attempts) {
			try {
				commitAttempt(attempt, base_file);
				final_tier = attempt;
				break;
			} catch (e) {
				log('[#3392] commit failed: ' + attempt, e);
				final_dropped = base_dropped;
			}
		}

		if (final_tier === null) {
			notify('error', PRINT_FAILED_MESSAGE);
			return { tier: 'none', dropped: final_dropped };
		}

		// 커밋에 성공한 뒤에만 이력을 1회 남긴다. 예외·rejection 은 기록만 한다.
		try {
			const history = deps.recordHistory();
			if (history && typeof history.then === 'function') history.then(noop, e => log('[#3392] label print history failed', e));
		} catch (e) {
			log('[#3392] label print history failed', e);
		}

		// #3392 : 실제로 빠진 출력만 알린다(목록·레이블 선택 조합별).
		if (final_tier === 'base' && final_dropped) notify('warning', want_list && want_labels ? EXTRAS_DROPPED_MESSAGE : want_list ? LIST_DROPPED_MESSAGE : LABEL_ONLY_DROPPED_MESSAGE);
		else if (final_tier === 'list' && final_dropped === 'label') notify('warning', LABEL_DROPPED_MESSAGE);

		return { tier: final_tier, dropped: final_dropped };
	}

	// settle 은 한 번만 실행된다. 즉시 settled 로 표시하고 진행 중 렌더를 놓는다.
	async function settle(tier, dropped) {
		if (settled) return;
		settled = true;
		releaseActiveRender();
		let outcome = { tier: 'none', dropped: null };
		try {
			outcome = await commitTier(tier, dropped);
		} catch (e) {
			log('[#3392] settle failed', e);
		} finally {
			resolve_run(outcome);
		}
	}

	// 팝업 닫기·ESC 는 실패가 아니므로 경고하지 않고 그 시점의 최선 단계만 커밋한다(C1-P2: toast 는 실패로 빠졌을 때만).
	function abandonExtras() {
		if (settled) return;
		settle(verified && verified.has_list ? 'list' : 'base', null);
	}

	// 기본 템플릿이 실패하면 부가 출력을 기다리지 않는다.
	base_promise.then(noop, () => settle('base', null));

	async function run() {
		// #3392 : 목록·레이블을 둘 다 고르지 않았으면 조회 없이 오늘과 같은 출력이다.
		if (!want_list && !want_labels) return settle('base', null);

		// b. 실제 포장 홀박스. 박스 대응(주 인보이스 번호)은 레이블을 고를 때만 묻는다.
		let invoices;
		let list;
		let whole_box_nos;
		try {
			const response = await deps.fetchWholeBoxData(invoice_nos, request_timeout_ms, want_labels ? main_invoice_no : undefined);
			if (settled) return;
			const response_data = response && response.data;
			invoices = normalizeWholeBoxInvoices(response_data, invoice_nos);
			list = buildWholeBoxList(invoices);
			// #3392 : 박스 대응은 맞지 않으면 null(끝 배치)일 뿐 실패가 아니다.
			package_box_nos = normalizePackageMapping(response_data, print_data.image);
			whole_box_nos = normalizeWholeBoxNos(response_data, invoices);
		} catch (e) {
			if (settled) return;
			log('[#3392] whole box data failed', e);
			return settle('base', base_dropped);
		}
		if (list.length === 0) return settle('base', null);
		list_known = true;

		// c. 새 템플릿 인스턴스로 UPS(+ 목록)를 미리 렌더하고 내용을 검사한다. 목록을 고르지 않았으면 목록 데이터를 넘기지 않는다(목록 쪽이 생기지 않는다).
		let ups_report;
		let page_names;
		const ups_data = want_list ? { ...print_data, wholeBoxList: list } : print_data;
		try {
			const base_file = await base_promise;
			if (settled) return;
			ups_report = deps.createUpsTemplate(base_file);
			await renderTracked(ups_report, ups_data);
			if (settled) return;
			const ups_snapshot = deps.snapshot(ups_report);
			const result = want_list
				? deps.verifyUpsList(ups_snapshot, { image_count: print_data.image.length, rows: list, list_page_name: WHOLE_BOX_LIST_PAGE_NAME })
				: deps.verifyUps(ups_snapshot, { image_count: print_data.image.length, list_page_name: WHOLE_BOX_LIST_PAGE_NAME });
			if (!result || !result.ok) throw new Error((want_list ? 'Whole box list' : 'UPS label') + ' is incomplete: ' + (result ? result.reason : 'no result'));
			// #3392 : 재배치 계획은 검증한 snapshot 의 쪽 이름으로 세운다.
			page_names = ups_snapshot.pages.map(page => page.name);
		} catch (e) {
			if (settled) return;
			log('[#3392] ' + (want_list ? 'whole box list' : 'ups label pre-render') + ' failed', e);
			return settle('base', base_dropped);
		}
		verified = { report: ups_report, data: ups_data, page_names, has_list: want_list };
		if (!want_labels) return settle('list', null);

		// d. 인보이스마다 새 레이블 템플릿을 단위 변환 뒤 순차 렌더하고 검사한다. 하나라도 어긋나면 레이블 전체를 버린다.
		const label_reports = [];
		const groups = [];
		try {
			const label_file = await deps.loadWholeBoxFile(request_timeout_ms);
			if (settled) return;
			let label_page_count = 0;
			for (const invoice of invoices) {
				const template = deps.createWholeBoxTemplate(label_file);
				const converted = deps.convertWholeBoxTemplate(template);
				if (!converted || !converted.ok) throw new Error('Whole box label template check failed: ' + (converted ? converted.reason : 'no result'));
				await renderTracked(template, invoice);
				if (settled) return;
				const snapshot = deps.snapshot(template);
				const result = deps.verifyWholeBoxLabels(snapshot, {
					rows: invoice.wholeBoxPrintList,
					so_dn: invoice.so_dn,
					required_names: WHOLE_BOX_TICKET_REQUIRED_NAMES,
				});
				if (!result || !result.ok) throw new Error('Whole box label is incomplete: ' + invoice.invoice_no + ' ' + (result ? result.reason : 'no result'));
				label_page_count += snapshot.pages.length;
				label_reports.push(template);
				groups.push({ invoice_no: invoice.invoice_no, page_count: snapshot.pages.length, box_nos: whole_box_nos.get(String(invoice.invoice_no)) || null });
			}
			const list_total = list.reduce((sum, row) => sum + row.box_qty, 0);
			if (label_page_count !== list_total) throw new Error('Whole box label count ' + label_page_count + ' != ' + list_total);
		} catch (e) {
			if (settled) return;
			log('[#3392] whole box label failed', e);
			// #3392 : 목록도 골랐으면 검증된 목록 단계로, 레이블만 골랐으면 base 로 내려간다.
			return want_list ? settle('list', 'label') : settle('base', base_dropped);
		}

		// e. 검증된 UPS + 목록 보고서 뒤에 레이블 쪽을 한 번에 붙인다. 재배치는 커밋 직전(commitAttempt)에 한다.
		try {
			label_reports.forEach(template => deps.appendPages(ups_report, template));
		} catch (e) {
			log('[#3392] whole box label merge failed', e);
			verified = null;
			return settle('base', base_dropped);
		}
		label_groups = groups;
		return settle(want_list ? 'full' : 'labels', null);
	}

	run().catch(e => {
		log('[#3392] whole box print failed', e);
		settle('base', list_known ? base_dropped : null);
	});

	return { promise, abandonExtras };
}
